import type { ShareTripFile } from '@itin/shared'
import { locales } from '@itin/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Logo } from '../components/Logo.js'
import { OfflineNotice } from '../components/OfflineNotice.js'
import { TripMap } from '../components/TripMap.js'
import { TripProgress } from '../components/TripProgress.js'
import { PlanUpdates, usePlanUpdates } from '../components/PlanUpdates.js'
import { ItemEntry, PlanDaySection } from '../components/PlanDay.js'
import { Button, Input } from '../components/ui.js'
import { api, ApiError, saveBlob } from '../lib/api.js'
import { formatDate, useLocale, useT } from '../lib/i18n.js'
import { useOfflineCopy } from '../lib/offline.js'

/**
 * Her site, as a constant.
 *
 * `agencies.branding` exists in the schema and is granted to the share role,
 * but the share contract projects only `agency.name` and nothing in the server
 * reads `branding` at all. Plumbing a domain through the contract, the service
 * and a settings UI is the right build for a second tenant and the wrong one
 * for the first — it turns ten minutes into three days to make a value
 * configurable that has exactly one possible value.
 *
 * When there IS a second agency: this moves into `branding`, the share contract
 * grows a field, and this constant becomes its fallback. Not before.
 */
const AGENCY_DOMAIN = 'longhitravel.com'
const agencyHref = `https://www.${AGENCY_DOMAIN}`

/**
 * What the traveller sees.
 *
 * No account, no app, no password. The link is the whole of the interaction.
 *
 * The emergency block is rendered above the itinerary rather than inside it:
 * somebody reaching for a consulate number is having a bad day and should not
 * have to scroll past Tuesday to find it.
 */
export function SharePage() {
  const { token = '' } = useParams()
  const t = useT()
  const { locale, setLocale } = useLocale()

  const plan = useQuery({
    queryKey: ['share', token],
    queryFn: () => api.getSharePlan(token),
    /**
     * The request goes out even with no connection, and that IS the offline
     * feature.
     *
     * React Query's default `networkMode: 'online'` does not fail an offline
     * query — it PAUSES it, and leaves `status: 'pending'` for as long as the
     * signal is gone. The service worker would be sitting on a perfectly good
     * copy of this itinerary and would never once be asked for it: the
     * traveller in the station gets "A carregar…" and nothing else, for ever.
     * `always` sends the request regardless and lets the worker answer it.
     */
    networkMode: 'always',
    /**
     * Bounded, which matters more now than it did. A bad link is still a bad
     * link and an `ApiError` never retries — but the previous rule retried
     * anything else without limit, so a traveller with no cached copy and no
     * signal would have sat on the loading state indefinitely instead of being
     * told what was wrong. Two attempts, then the page says something true.
     */
    retry: (count, error) => (error instanceof ApiError ? false : count < 2),
  })

  // Above the early returns, because hooks are not conditional. It takes the
  // plan as `undefined` while the query is in flight and reports nothing, which
  // is also what it does for a reader with no stored baseline.
  const updates = usePlanUpdates(token, plan.data?.value)

  // Registers the worker and hands it the list of what this page used. Driven
  // off the plan having arrived, because a page that has not rendered yet has
  // not loaded the fonts or the tiles that make it readable.
  useOfflineCopy(plan.data !== undefined)

  if (plan.isPending) {
    return (
      <main className="mx-auto max-w-2xl p-6">
        <p className="text-sm text-slate-500">{t('common.loading')}</p>
      </main>
    )
  }

  if (plan.isError) {
    /*
     * "Not a valid link" and "no signal" are different sentences, and telling a
     * traveller the first when the second is true sends them to their advisor
     * over a working link. An `ApiError` came from a server that answered; a
     * bare failure means the request never got out — and if it never got out
     * and the worker had nothing, this itinerary was never opened on this
     * device with a connection, which is the one thing they can act on.
     */
    const answered = plan.error instanceof ApiError
    return (
      <main className="mx-auto max-w-2xl p-6">
        <p
          data-testid="share-unavailable"
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          {answered ? t('sharePage.notFound') : t('offline.notStored')}
        </p>
      </main>
    )
  }

  const { trip, agency, participant, days, loose } = plan.data.value
  const hasLoose = loose.items.length > 0
  const mapped = [...days.flatMap((d) => d.items), ...loose.items]

  return (
    <main className="min-h-dvh bg-white">
      {/*
        The navy band her clients already know, then a light page. This is the
        whole "still on Ana's website" instruction: a link that opens into a
        white form reads as software, and a link that opens into her letterhead
        reads as the document she prepared for you.
      */}
      {/*
        Tighter on a phone.

        On a laptop this band is a title page and it should be. On a 390×844
        phone it was 480px — the traveller taps Ana's WhatsApp link and gets a
        screen of branding, with day one below the fold. It still opens into her
        letterhead, just at a size that leaves room for the itinerary.
      */}
      {/*
        `no-print` is NOT on this element, and that is the fix.

        It used to be, alongside `brand-ground` — and `.no-print { display:none
        !important }` beats everything, so the print rules written specifically
        to keep her mark on paper (styles.css, under @media print) could never
        fire. They were dead code. Every PDF this product had ever produced was
        completely anonymous: no logo, no "preparado por", nothing identifying
        who made it.

        The PDF is the copy that leaves the sharing context. It gets forwarded
        on WhatsApp and printed for a folder, and it is the version most likely
        to be seen by somebody who is not Ana's client yet. It should carry her
        name. `no-print` now sits on the controls inside, which are the things
        that genuinely mean nothing on paper.
      */}
      <header className="brand-ground px-5 py-6 text-center sm:px-6 sm:py-10">
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-3 sm:gap-4">
          <Logo variant="full" />
          {/* testid because the colophon at the foot of the page now contains
              this same phrase as a substring, so text alone is ambiguous. */}
          <p data-testid="share-prepared-by" className="label-caps text-gold">
            {t('sharePage.preparedBy', { agency: agency.name })}
          </p>
          <h1 className="text-2xl text-cream sm:text-4xl" data-testid="share-greeting">
            {participant
              ? t('sharePage.greeting', { name: participant.displayName })
              : t('sharePage.anonymous')}
          </h1>
          <p className="rule-diamond w-full max-w-xs text-gold" aria-hidden="true">
            ◆
          </p>
          <p className="text-lg text-cream/85 italic sm:text-xl" data-testid="share-trip-title">
            {trip.title}
          </p>
          <p className="text-sm text-cream/60">
            {t('trips.dates', {
              start: formatDate(trip.startDate, locale),
              end: formatDate(trip.endDate, locale),
            })}
          </p>

          {/*
            The traveller chooses the language, and until now could not.
            Ana's clients are mostly Brazilian but not all of them, and the page
            was picking from the browser with no way to disagree — which is how
            a Portuguese itinerary ended up with English furniture around it.
          */}
          {/* The language toggle and the print button. Meaningless on paper —
              so THIS is what `no-print` belongs on. */}
          <div className="no-print mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm">
            <div className="flex items-center gap-1">
              {locales.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setLocale(option)}
                  aria-current={locale === option}
                  className={`flex min-h-11 items-center rounded px-3 transition-colors sm:min-h-0 sm:py-1 ${
                    locale === option
                      ? 'bg-gold/20 text-cream'
                      : 'text-cream/50 hover:text-cream/80'
                  }`}
                >
                  {option === 'pt-BR' ? 'Português' : 'English'}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => window.print()}
              className="flex min-h-11 items-center rounded border border-cream/25 px-3 text-cream/70 transition-colors hover:border-gold hover:text-cream sm:min-h-0 sm:py-1"
            >
              {t('sharePage.print')}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-2xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">

      {/*
        Before anything else on the page, including what changed.

        "Three things moved" is only worth reading once you know how old the
        document making the claim is. Read the other way round — a change banner
        above a copy that turns out to be four days old — the traveller has
        already decided the page is current by the time they learn it is not.
      */}
      <OfflineNotice storedAt={plan.data.storedAt} />

      {/*
        First on the page, above "where am I".

        The two answer different questions and only one of them is urgent. A
        traveller who already read this itinerary is holding a document they
        believe they know; if a flight moved, that belief is the thing to
        correct before anything else — and it may well be the very entry
        TripProgress is about to announce as "next". Below the progress card it
        would be the second thing read, under a line that had already told them
        confidently where they are going.

        It renders nothing when there is nothing to say, so on the overwhelming
        majority of visits this is not on the page at all.
      */}
      <PlanUpdates changes={updates.changes} onDismiss={updates.dismiss} />

      {/*
        Above the map, because "where am I in this" is the first question a
        traveller mid-trip has and the map is the second.
      */}
      {days.length > 0 && (
        <TripProgress
          days={days}
          timeZone={trip.timezone}
          onJumpToDay={(index) => {
            document
              .getElementById(`day-${index}`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }}
        />
      )}

      <TripMap token={token} items={mapped} />


      <section data-testid="share-plan">
        {days.length === 0 && <p className="text-sm text-slate-500">{t('plan.empty')}</p>}
        {days.map((day, index) => (
          <PlanDaySection
            key={day.id}
            day={day}
            index={index}
            timeZone={trip.timezone}
            emergencyInfo={trip.emergencyInfo}
            changes={updates.changes}
          />
        ))}
      </section>

      {hasLoose && (
        <section className="border-t border-slate-200 pt-4">
          <h3 className="label-caps text-slate-500">{t('plan.unscheduled')}</h3>
          <ul className="mt-2 flex flex-col divide-y divide-slate-100">
            {loose.items.map((item) => (
              <ItemEntry
                key={item.id}
                item={item}
                timeZone={trip.timezone}
                changes={updates.changes}
              />
            ))}
          </ul>
        </section>
      )}

      <DocumentsSection token={token} />

      {/*
        Signed at the end.

        A document that runs to nine days should say who made it where it
        finishes, not only where it starts — and on paper the letterhead may be
        several pages back. The URL is a real anchor so the print rule that
        appends `a[href]` targets renders it as text on paper for free.

        Deliberately quiet, and deliberately not a pitch. Genitravel puts a
        WhatsApp button and its own wordmark here, but that is a vendor
        advertising to the advisor's client. This is the advisor signing her own
        work, which is a different thing and only works if it reads as one.
      */}
      <footer
        data-testid="share-colophon"
        className="border-t border-slate-200 pt-5 text-center text-sm text-slate-500"
      >
        {t('sharePage.colophon', { agency: agency.name })}{' '}
        <a
          className="text-slate-700 underline decoration-gold underline-offset-4 hover:text-slate-900"
          href={agencyHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {AGENCY_DOMAIN}
        </a>
      </footer>
      </div>
    </main>
  )
}

/**
 * Vouchers, insurance certificates, tickets — what the traveller can read from
 * the plan itself. Structurally absent from this list: anything under an
 * identity category. `travel_documents` (7.16) never reaches a share link at
 * all, by grant, not by a check here that could be forgotten.
 */
function DocumentsSection({ token }: { token: string }) {
  const t = useT()

  const files = useQuery({
    queryKey: ['shareFiles', token],
    queryFn: () => api.listShareFiles(token),
    // Same reason as the plan: paused offline, this list would never be asked
    // for and the section would silently not exist. See SharePage above.
    networkMode: 'always',
  })

  if (!files.data || files.data.length === 0) return null

  return (
    <section className="border-t border-slate-200 pt-4" data-testid="share-documents">
      <h3 className="text-base font-semibold">{t('files.title')}</h3>
      <ul className="mt-2 flex flex-col divide-y divide-slate-200">
        {files.data.map((file) => (
          <ShareFileRow key={file.id} token={token} file={file} />
        ))}
      </ul>
    </section>
  )
}

function ShareFileRow({ token, file }: { token: string; file: ShareTripFile }) {
  const t = useT()
  const [unlocking, setUnlocking] = useState(false)
  const [code, setCode] = useState('')

  const download = useMutation({
    mutationFn: (unlockCode: string | undefined) => api.downloadShareFile(token, file.id, unlockCode),
    /**
     * Fail, rather than wait.
     *
     * A paused mutation keeps `isPending` true, so offline the button sat on
     * "A baixar…" indefinitely — a spinner that will never stop is a worse
     * answer than a sentence saying this one needs a connection. The file bytes
     * genuinely are unavailable offline: the download is a POST, because the
     * unlock code must not travel in a URL, and a POST has nothing cached.
     */
    networkMode: 'always',
    onSuccess: (blob) => {
      saveBlob(blob, file.filename)
      setUnlocking(false)
      setCode('')
    },
  })

  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-col">
        <span className="font-medium">{file.filename}</span>
        <span className="text-xs text-slate-500">
          {t(`files.category.${file.category}`)}
          {file.sensitivity === 'sensitive' && ` · ${t('files.sensitiveBadge')}`}
        </span>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {file.sensitivity === 'shareable' || unlocking ? (
          <>
            {file.sensitivity === 'sensitive' && (
              <Input
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('files.field.unlockCode')}
                className="w-32"
                autoFocus
              />
            )}
            <Button
              variant="quiet"
              disabled={download.isPending}
              onClick={() => download.mutate(file.sensitivity === 'sensitive' ? code : undefined)}
            >
              {download.isPending
                ? t('files.downloading')
                : file.sensitivity === 'sensitive'
                  ? t('files.unlockSubmit')
                  : t('files.download')}
            </Button>
          </>
        ) : (
          <Button variant="quiet" onClick={() => setUnlocking(true)}>
            {t('files.enterCode')}
          </Button>
        )}
      </div>
      {download.isError && (
        <p className="text-xs text-red-600">
          {/*
            Three outcomes, not two. The list of documents is cached and opens
            offline, but the bytes are a POST and never are — so with no signal
            this used to read "Erro interno", which sends somebody hunting for a
            fault that is the aeroplane mode switch. A failure that never
            reached a server is not an ApiError, and that is how it is told
            apart.
          */}
          {!(download.error instanceof ApiError)
            ? t('offline.needsConnection')
            : download.error.code === 'not_found'
              ? t('files.unlockFailed')
              : t('error.internal')}
        </p>
      )}
    </li>
  )
}
